// The tree by wall (round 4 frames f3a and f5a): every stop in walk order, grouped "Wall · kind of item", Whole room
// last; each row its number, name, tags (changes, to fix, unverified, add-on or "for L-02"), and "makes 3 · $714.40"
// while closed. A small chevron opens a row to the children it makes, each with its own verified / unverified / to fix
// tag and price; a child opens its own panel. On the walk each row carries its check circle and the next stop its flag.

import type { Finding, FlagRow, ResolvedItem, WalkStop } from '@zerogap/engine';
import { itemDeviations, itemFindings } from '@zerogap/engine';
import { inches } from '../../../lib/format';
import type { Row } from '../../../lib/api';
import { Money } from '../../../ui/kit';
import { AiChangedTag, UnverifiedTag } from '../../../ui/unverified';
import type { JobCtx } from '../Workspace';
import { useState } from 'react';
import { toast, updateRow } from '../../../lib/api';
import { useSession } from '../../../lib/session';
import { addOnShort, wallShort, type WalkModel } from './model';

export interface TreeProps {
  ctx: JobCtx;
  model: WalkModel;
  mode: 'items' | 'quick' | 'walk';
  /** The wall in view (a View id), or 'room'; rows on other walls read grey, Whole room never. */
  wall: string | null;
  selectedId: string | null;
  open: Set<string>;
  onToggle: (id: string) => void;
  onPick: (item: ResolvedItem) => void;
  findings: Finding[];
  /** The job's flags: an open one on an item counts in its to-fix (2026-10-24). */
  flags: FlagRow[];
  users: Row[];
  nextId: string | null;
  onlyMoved: boolean;
  /** Items still to place on the sheet in view (a replaced sheet), by id. */
  toPlace: Set<string>;
  /** "Not placed yet" (the Views pack, 2026-10-24): the top-level items with a mark on no View, by number; on Items built they are listed after the walls, before Whole room. */
  notPlaced: ResolvedItem[];
  /** The item being placed right now (its row reads "placing"), and Place on this sheet, offered while a View is in view. */
  placingId?: string | null;
  onPlace?: (item: ResolvedItem) => void;
  /** Select to edit: the rows picked, and the toggle. */
  picking: string[] | null;
  onPickToggle: (id: string) => void;
  /** Open the picked items together. */
  onEditPicked?: () => void;
}

/** A child's name with its size when it has one: Door · 17 7/32 x 30 3/8. */
export function sizedName(i: ResolvedItem): string {
  const w = i.inputs.find((x) => x.key === 'width' && !x.hidden)?.value;
  const h = i.inputs.find((x) => x.key === 'height' && !x.hidden)?.value;
  const size = w !== null && w !== undefined && w !== '' && h !== null && h !== undefined && h !== '' ? ` · ${inches(w).replace(/"$/, '')} x ${inches(h).replace(/"$/, '')}` : '';
  return `${i.name || i.catalog_item_name || ''}${size}`;
}

export function counts(ctx: JobCtx, model: WalkModel, item: ResolvedItem, findings: Finding[], flags: FlagRow[] = []) {
  const row = model.rowOf(item.id) as any;
  const res = model.resOf(item.id);
  const devs = itemDeviations(item, res, ctx.r.lib!, row);
  const finds = itemFindings(item, findings, row, flags);
  return { devs, finds, changes: devs.length, toFix: finds.filter((f) => !f.answered).length };
}

export function Tree(p: TreeProps) {
  const { ctx, model, mode } = p;
  const walk = mode !== 'items';
  const kids = (id: string) => model.res.items.filter((i) => i.parent_item_id === id).sort((a, b) => (a.child_index ?? 0) - (b.child_index ?? 0) || a.number.localeCompare(b.number, undefined, { numeric: true }));
  const addOnName = (id: string) => String(ctx.data.addOns.find((a) => a.id === id)?.name ?? 'add-on');
  const numberOf = (id: string) => model.res.items.find((i) => i.id === id)?.number ?? ctx.data.items.find((x) => x.id === id)?.number ?? '';
  const jobGroup = model.res.groups.find((g) => g.kind === 'job');
  const top = model.stops.filter((s) => s.item).length;
  const shown = (s: WalkStop) => !p.onlyMoved || model.stateOf(s) === 'cleared';

  const notPlacedIds = new Set(walk ? [] : p.notPlaced.map((i) => i.id));
  const itemRow = (st: WalkStop, item: ResolvedItem, off: boolean, unplaced = false) => {
    const children = kids(item.id);
    const isOpen = children.length > 0 && p.open.has(item.id);
    const c = counts(ctx, model, item, p.findings, p.flags);
    const state = model.stateOf(st);
    const notIn = model.notIn(item);
    const row = model.rowOf(item.id);
    const picked = p.picking?.includes(item.id) ?? false;
    const sel = p.picking ? picked : p.selectedId === item.id;
    const press = () => (p.picking ? p.onPickToggle(item.id) : p.onPick(item));
    return (
      <div key={item.id}>
        <div
          className={`li${off ? ' off' : ''}${walk && notIn ? ' nob' : ''}${sel ? ' sel' : ''}${item.replaces_item_id ? ' alt' : ''}${walk ? '' : ' nock'}`}
          role="button"
          tabIndex={0}
          onClick={press}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
              e.preventDefault();
              press();
            }
          }}
        >
          {walk && <i className={`ck${state === 'checked' ? ' on' : ''}`} aria-label={state === 'checked' ? 'checked' : 'not checked'} />}
          <span
            className={`chev${isOpen ? ' open' : ''}${children.length ? '' : ' none'}`}
            role={children.length ? 'button' : undefined}
            aria-label={children.length ? (isOpen ? `Hide what ${item.number} makes` : `Show what ${item.number} makes`) : undefined}
            onClick={(e) => {
              if (!children.length) return;
              e.stopPropagation();
              p.onToggle(item.id);
            }}
          />
          <span className="no">{item.number}</span>
          <span className="nm" title={item.name}>
            {item.name || item.catalog_item_name}
          </span>
          <span className="tags">
            {walk && p.nextId === item.id && <span className="tag next">next</span>}
            {walk && notIn && <span className="tag off">not in {ctx.build?.name ?? 'this build'}</span>}
            {state === 'cleared' && <span className="tag moved" title="An edit cleared its Checked mark: a recheck lists it">moved</span>}
            {c.changes > 0 && <span className="tag chg">{c.changes} {c.changes === 1 ? 'change' : 'changes'}</span>}
            {c.toFix > 0 && <span className="tag fix">{c.toFix} to fix</span>}
            {/* the hatch carries "unverified" on every row; the key at the top names it once (F5, 2026-10-24) */}
            {item.is_custom ? <span className="tag unv mark" title="Unverified · custom item: priced from what was typed on it" aria-label="unverified" /> : item.unverified.any && <UnverifiedTag items={[item, ...children]}>{''}</UnverifiedTag>}
            {row?.unverified_since && <AiChangedTag table="job_item" row={row as any} users={p.users as any[]} short />}
            {item.replaces_item_id ? (
              <span className="tag addon" title={`${addOnName(item.add_on_id ?? '')} · stands in for ${numberOf(item.replaces_item_id)} while the add-on is on`}>
                for {numberOf(item.replaces_item_id)}
              </span>
            ) : (
              item.add_on_id && (
                <span className="tag addon" title={`${addOnName(item.add_on_id)} · priced only with this add-on on`}>
                  {addOnShort(addOnName(item.add_on_id))}
                </span>
              )
            )}
            {p.toPlace.has(item.id) && !off && <span className="tag fix">place on the sheet</span>}
            {unplaced && p.placingId === item.id && <span className="tag chg">placing</span>}
            {unplaced && p.placingId !== item.id && p.onPlace && !p.picking && (
              <button
                type="button"
                className="btn placebtn"
                title="Turn placing on for this item: click the sheet where it sits"
                onClick={(e) => {
                  e.stopPropagation();
                  p.onPlace!(item);
                }}
              >
                Place on this sheet
              </button>
            )}
          </span>
          <span className="lpr">
            {children.length > 0 && !isOpen && <span className="ct">makes {children.length} · </span>}
            <Money cents={item.price_cents} />
          </span>
        </div>
        {isOpen &&
          children.map((k) => {
            const kc = counts(ctx, model, k, p.findings, p.flags);
            return (
              <div
                key={k.id}
                className={`li part${off ? ' off' : ''}${p.selectedId === k.id && !p.picking ? ' sel' : ''}${walk ? '' : ' nock'}`}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!p.picking) p.onPick(k);
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget && !p.picking) {
                    e.preventDefault();
                    p.onPick(k);
                  }
                }}
              >
                {walk && <i className={`ck part${state === 'checked' ? ' on' : ''}`} />}
                <span className="chev none" />
                <span className="no">{k.number}</span>
                <span className="nm" title={sizedName(k)}>
                  {sizedName(k)}
                </span>
                <span className="tags">
                  {kc.toFix > 0 ? (
                    <span className="tag fix">{kc.toFix} to fix</span>
                  ) : kc.changes > 0 ? (
                    <span className="tag chg">{kc.changes} held</span>
                  ) : k.is_custom ? (
                    <span className="tag unv mark" title="Unverified · custom item" aria-label="unverified" />
                  ) : k.unverified.any ? (
                    <UnverifiedTag items={[k]}>{''}</UnverifiedTag>
                  ) : (
                    <span className="tag ok">verified</span>
                  )}
                </span>
                <span className="lpr">
                  <Money cents={k.rollup_price_cents} />
                </span>
              </div>
            );
          })}
      </div>
    );
  };

  const jobRow = (st: WalkStop) => {
    if (!jobGroup) return null;
    const route = st.kind === 'job_route';
    const lines = route ? jobGroup.steps : jobGroup.bom;
    const cents = route ? jobGroup.steps.reduce((n, x) => n + x.cents, 0) : jobGroup.bom.reduce((n, x) => n + x.cents, 0);
    const state = model.stateOf(st);
    const sel = p.selectedId === st.key;
    const press = () => !p.picking && p.onPick({ id: st.key } as ResolvedItem);
    return (
      <div
        key={st.key}
        className={`li${sel ? ' sel' : ''}${walk ? '' : ' nock'}`}
        role="button"
        tabIndex={0}
        onClick={press}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault();
            press();
          }
        }}
      >
        {walk && <i className={`ck${state === 'checked' ? ' on' : ''}`} />}
        <span className="chev none" />
        <span className="no">{route ? 'Route' : 'BOM'}</span>
        <span className="nm">{route ? `Job route, ${lines.length} ${lines.length === 1 ? 'step' : 'steps'}` : `Job BOM · ${jobGroup.bom.map((b) => b.description).slice(0, 2).join(', ')}`}</span>
        <span className="tags">
          {walk && p.nextId === st.key && <span className="tag next">next</span>}
          {ctx.data.job?.unverified_since && <AiChangedTag table="job" row={ctx.data.job as any} users={p.users as any[]} short />}
        </span>
        <span className="lpr">
          <Money cents={cents} />
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="lhead">
        <b>Items by wall</b>
        <span className="only">{top}</span>
        <span className="sp" />
        {/* the key: the hatch is "unverified", named here once, not on every row (F5, 2026-10-24) */}
        {model.res.items.some((i) => i.unverified.any || i.is_custom) && (
          <span className="only lkey">
            <span className="tag unv mark" aria-hidden /> unverified
          </span>
        )}
      </div>
      {p.picking && (
        <div className="pickbar">
          <span>{p.picking.length === 0 ? 'Tap the items to edit together.' : `${p.picking.length} picked: ${p.picking.map((id) => model.res.items.find((i) => i.id === id)?.number).join(', ')}`}</span>
          {p.picking.length > 0 && p.onEditPicked && (
            <button type="button" className="btn pri" onClick={p.onEditPicked}>
              Edit {p.picking.length} together
            </button>
          )}
        </div>
      )}
      <div className="list">
        {model.walls.map((w) => {
          const room = !w.view;
          const off = !room && !!p.wall && p.wall !== 'room' && w.view!.id !== p.wall;
          // on Items built an unplaced item is listed under Not placed yet, not under Whole room; on the walk it is walked under Whole room as before
          const stops = w.stops.filter(shown).filter((st) => !(room && st.item && notPlacedIds.has(st.item.id)));
          const out: React.ReactNode[] = [];
          if (room && !walk && p.notPlaced.length > 0) {
            out.push(
              <div key="np-h" className="lgrp notplaced">
                Not placed yet · {p.notPlaced.length}
                <span className="only">a mark on no View{p.onPlace ? '; Place on this sheet turns placing on for the item' : ''}</span>
              </div>,
            );
            for (const i of p.notPlaced) out.push(itemRow({ kind: 'item', key: i.id, item: i, view_id: null, group: 'Not placed yet' }, i, false, true));
          }
          if (stops.length === 0) return out.length ? <div key="np">{out}</div> : null;
          let last = '';
          if (room) out.push(<div key="room-h" className="lgrp room">{walk ? 'Whole room · no wall, walked last' : 'Whole room · no wall'}</div>);
          for (const st of stops) {
            if (!room && st.item && !st.item.replaces_item_id) {
              const k = model.kindOf(st.item);
              if (k !== last) {
                last = k;
                out.push(
                  <div key={`h-${w.view!.id}-${k}`} className={`lgrp${off ? ' off' : ''}`}>
                    {wallShort(w.name)} · {k}
                  </div>,
                );
              }
            }
            out.push(st.item ? itemRow(st, st.item, off) : jobRow(st));
          }
          return <div key={w.view?.id ?? 'room'}>{out}</div>;
        })}
        {model.stops.length === 0 && <p className="only" style={{ padding: '10px 14px' }}>No items yet. + item adds one; Add many types several at once.</p>}
        <RetiredItems ctx={ctx} />
        {p.onlyMoved && !model.stops.some(shown) && <p className="only" style={{ padding: '10px 14px' }}>Nothing moved: no edit has cleared a Checked mark since the last check.</p>}
      </div>
    </>
  );
}


/** Items an AI employee retired (2026-10-23): out of every price and off the sheet, kept behind Show retired, back with Restore (a person, or the employee's restore_item). */
function RetiredItems({ ctx }: { ctx: JobCtx }) {
  const [open, setOpen] = useState(false);
  const s = useSession();
  const retired = ctx.data.items.filter((i) => i.retired_at && !i.parent_item_id).sort((a, b) => String(a.number ?? '').localeCompare(String(b.number ?? ''), undefined, { numeric: true }));
  if (retired.length === 0) return null;
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  return (
    <div className="retired-items">
      <button type="button" className="lgrp fold" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className={`chev${open ? ' open' : ''}`} /> Show retired · {retired.length}
      </button>
      {open &&
        retired.map((i) => (
          <div key={i.id} className="li off nock retired-row">
            <span className="no">{i.number}</span>
            <span className="nm" title={i.name}>
              {i.name}
            </span>
            <span className="tags">
              <span className="tag off" title={`Retired ${String(i.retired_at).slice(0, 10)}: out of every price, off the sheet`}>
                retired {String(i.retired_at).slice(5, 10)}
              </span>
            </span>
            <span className="lpr">
              {canEdit && (
                <button type="button" className="btn" onClick={() => void updateRow('job_item', i.id, { retired_at: null, retired_by: null }).then(() => toast(`${i.number} is back on the job.`))}>
                  Restore
                </button>
              )}
            </span>
          </div>
        ))}
    </div>
  );
}